import { useState, useCallback } from 'react'
import { useUserClientState } from './'
import { useQueryClient, useQuery, useMutation, useMutationState } from '@tanstack/react-query'
import { useUpdateUsersCache } from 'state/users'
import { get, post, patch, remove } from 'api'

const namespace = 'user'

export const getUserQuery = (id, options = {}) => ({
    ...options,
    queryKey: [namespace, id],
    queryFn: () => get({ path: `/users/${id}` })
})

export const useUser = (options = {}) => {
    const clientState = useUserClientState(state => ({
        queryOptions: state.queryOptions,
        id: state.id
    }), true)

    const queryOptions = {
        ...(clientState?.queryOptions ?? null),
        ...(options?.queryOptions ?? null)
    }

    const [enabled, setEnabled] = useState(queryOptions?.enabled ?? true)
    const enable = useCallback(() => setEnabled(true), [])

    const id = options?.id ?? clientState?.id
    const deleted = useUserDeleted()

    const queryEnabled = enabled && !!id && !deleted

    const {
        data,
        ...rest
    } = useQuery({
        ...getUserQuery(id, queryOptions),
        enabled: queryEnabled
    })

    return {
        ...rest,
        data,
        item: data?.response ?? null,
        enabled: queryEnabled,
        enable
    }
}

export const useFetchUser = () => {
    const queryClient = useQueryClient()

    return (id, queryOptions) => {
        if(!id) {
            return null
        }

        return queryClient.fetchQuery(getUserQuery(id, {
            ...(queryOptions ?? null),
            staleTime: 0 // As it is imperative, there will be no live subscription to new data
        }))
    }
}

export const useCreateUser = (options = {}) => {
    const queryClient = useQueryClient()
    const updateUsersCache = useUpdateUsersCache()

    return useMutation({
        gcTime: 15 * 1000,
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'create'],
        mutationFn: ({ body }) => post({
            path: '/users',
            body
        }),
        onSuccess: result => {
            if(!result.ok) {
                return
            }

            queryClient.setQueryData(
                getUserQuery(result.response.id).queryKey,
                result
            )

            updateUsersCache('invalidateQueries')
        }
    })
}

export const useUpdateUser = (options = {}) => {
    const queryClient = useQueryClient()
    const clientState = useUserClientState(state => ({ id: state.id }), true)
    const updateUsersCache = useUpdateUsersCache()

    return useMutation({
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'update'],
        mutationFn: ({ id, body }) => patch({
            path: `/users/${body?.id ?? id ?? clientState?.id}`,
            body
        }),
        onSuccess: result => {
            if(!result.ok) {
                return
            }

            queryClient.setQueryData(
                getUserQuery(result.response.id).queryKey,
                result
            )

            updateUsersCache('invalidateQueries')
        }
    })
}

export const useDeleteUser = (options = {}) => {
    const queryClient = useQueryClient()
    const clientState = useUserClientState(state => ({ id: state.id }), true)
    const updateUsersCache = useUpdateUsersCache()

    return useMutation({
        gcTime: 0,
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'delete'],
        mutationFn: ({ id }) => remove({
            path: `/users/${id ?? clientState?.id}`,
            returnsData: false
        }),
        onSuccess: ({ ok }, { id }) => {
            if(!ok) {
                return
            }

            queryClient.removeQueries({
                queryKey: getUserQuery(id ?? clientState?.id).queryKey,
                exact: true
            })

            updateUsersCache('invalidateQueries')
        }
    })
}

export const useUserDeleted = () => {
    const [deleted = false] = useMutationState({
        filters: {
            mutationKey: [namespace, 'delete'],
            exact: true,
            status: 'success'
        },
        select: ({ state }) => !!state?.data?.ok
    })

    return deleted
}